/**
 * The one reading of which suppliers an RFQ is actually working with.
 *
 * An RFQ carries a LIST of BOM documents, not one: re-extracting documents or
 * re-submitting sourcing criteria stores another BOM for the same RFQ, and no
 * document is ever marked the winner (every BOM is written `isActive: false`),
 * so `boms[0]` is simply the newest row — which may hold components whose
 * `supplierCandidates` were never filled in, while the research, the shortlist
 * and the live conversations sit on an older document. Reading one BOM
 * therefore answers "is there a shortlist?" with a confident, wrong "no".
 *
 * Every consumer of that question — the journey rail, the readiness strip, the
 * outreach threads — reads the candidates through here instead, so they cannot
 * disagree about the same suppliers.
 */

/** Statuses a candidate carries once the buyer has put them on the shortlist. */
export const SHORTLISTED_STATUSES = [
  'SHORTLISTED', 'CONTACTED', 'AWAITING_REPLY', 'RESPONDED', 'IN_NEGOTIATION', 'AWARDED',
];

const hasConversation = (candidate: any): boolean =>
  Array.isArray(candidate?.conversations) && candidate.conversations.length > 0;

/**
 * True when the buyer has committed to this supplier. A shortlist status is
 * the normal proof; a conversation is proof too, because an inquiry only ever
 * goes out to a shortlisted supplier — a candidate whose status was later
 * rewritten by a research re-run is still one the buyer wrote to. The legacy
 * `isShortlisted` flag is honoured for research payloads that still carry it.
 */
export const isCandidateShortlisted = (candidate: any): boolean =>
  SHORTLISTED_STATUSES.includes(String(candidate?.status || '').toUpperCase()) ||
  Boolean(candidate?.isShortlisted) ||
  hasConversation(candidate);

/**
 * Whether the platform holds an address for this supplier. The address itself
 * is never sent to the browser — the API replaces it with `hasContactEmail`,
 * so reading `contactEmail` here would report every supplier as unreachable.
 */
export const candidateHasContactEmail = (candidate: any): boolean =>
  Boolean(candidate?.hasContactEmail ?? candidate?.contactEmail);

/** An empty array is missing data, not an answer — `[] || fallback` is not. */
const nonEmpty = (list: any): any[] | null =>
  Array.isArray(list) && list.length > 0 ? list : null;

/**
 * Every component list the RFQ payload carries, newest BOM first, with the
 * backend's cross-BOM `analysisData` roll-up last as the fallback the outreach
 * step already relies on.
 */
const componentSources = (rfq: any): any[][] => {
  const sources: any[][] = [];
  (rfq?.boms || []).forEach((bom: any) => {
    const components = nonEmpty(bom?.components);
    if (components) sources.push(components);
  });
  const analysis = nonEmpty(rfq?.analysisData?.components) || nonEmpty(rfq?.extractedData?.components);
  if (analysis) sources.push(analysis);
  return sources;
};

/** The RFQ's parts list — the newest BOM that actually holds components. */
export const rfqComponents = (rfq: any): any[] => componentSources(rfq)[0] || [];

/** Identity of a component across BOM documents (a clone keeps its ids). */
const componentKey = (component: any, index: number): string =>
  String(
    component?.componentId || component?.id || component?._id ||
    component?.partNumber || component?.name || `index-${index}`
  );

/** Conversations outrank a status, and a status outranks a bare row. */
const candidateRank = (candidate: any): number =>
  hasConversation(candidate) ? 2 : isCandidateShortlisted(candidate) ? 1 : 0;

/**
 * Every supplier candidate on the RFQ, each counted once. The same supplier
 * appears on every BOM clone of its component; the furthest-along record wins,
 * so a candidate-less clone can never mask a shortlist that exists.
 */
export const rfqSupplierCandidates = (rfq: any): any[] => {
  const byKey = new Map<string, any>();
  componentSources(rfq).forEach((components) => {
    components.forEach((component: any, index: number) => {
      const key = componentKey(component, index);
      (component?.supplierCandidates || []).forEach((candidate: any) => {
        const name = String(candidate?.supplierName || candidate?.name || '').trim().toLowerCase();
        const candidateKey = `${key}::${name}`;
        const seen = byKey.get(candidateKey);
        if (!seen || candidateRank(candidate) > candidateRank(seen)) byKey.set(candidateKey, candidate);
      });
    });
  });
  return Array.from(byKey.values());
};
